import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import ApiEndpointPlayground from 'components/apiKeyScope/ApiEndpointPlayground';
import SubmitLeadTester from './SubmitLeadTester';
import SellerApiKeyPanel from 'views/admin/main/profile/settings/components/SellerApiKeyPanel';
import { CONFIG } from 'variables/config';
import { useIndustries, resolveFields, resolveMandatoryFields } from 'services/industryService';
import { GENERIC_API_MD_DOC_URL, buildGenericApiAgentInstructions } from 'services/sellerAgentPrompt';
import { fieldExampleValue, EMAIL_FIELD_REGEX, TEST_EMAIL } from 'services/formFieldExamples';
import { AccountPrice, accountPriceFor, fetchAccountPrices, PRICES_PATH } from 'services/integrations';
import { KindLogo } from '../formTemplates/integrationKinds';

/**
 * "Envoi par API" (`/seller-api-docs`), reached from Intégrations: a step-by-step guide — 1. your
 * API key (created and managed right here), 2. the product, 3. sending a lead, 4. testing without a
 * real lead, 5. reading the answer — then, always visible below, the list of products and their fields
 * (GET /lead/api/products, which step 3 points to) and instructions for an AI agent. The key shown in
 * step 1 is inserted in every example.
 */

// Kept in this browser only, so the examples and testers stay filled in between visits.
const API_KEY_STORAGE = 'leadrs-seller-api-tester-key';
const API_URL = CONFIG.apiUrl.replace(/\/$/, '');
const DISPLAY_API_URL = API_URL === 'https://api-leadrs-co.osc-fr1.scalingo.io' ? 'https://api.leadrs.co' : API_URL;

interface FormBuilderField {
    id: string;
    name: string;
    type?: string;
    options?: string[];
}

const card = 'rounded-[18px] border border-[#EDE7DE] bg-white p-6';
const code = 'rounded bg-[#F1ECE4] px-1.5 py-0.5 font-jbmono text-[12.5px] text-[#2E2A24]';

const Step = ({ n, title, intro, children }: { n: number; title: string; intro?: React.ReactNode; children: React.ReactNode }) => (
    <section className={card}>
        <div className="flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#241C5C] font-hanken text-[13px] font-bold text-white">{n}</span>
            <div className="min-w-0 flex-1">
                <h2 className="font-newsreader text-[24px] font-medium leading-tight text-[#14110E]">{title}</h2>
                {intro && <p className="mt-1 max-w-[720px] font-hanken text-[14px] leading-relaxed text-[#6B6357]">{intro}</p>}
                <div className="mt-4 flex flex-col gap-4">{children}</div>
            </div>
        </div>
    </section>
);

const CopyBlock = ({ text }: { text: string }) => {
    const [copied, setCopied] = useState(false);
    return (
        <div className="relative">
            <pre className="max-h-80 overflow-auto rounded-[12px] border border-[#EDE7DE] bg-[#F7F5F1] px-4 py-3 pr-24 font-jbmono text-[12px] leading-relaxed text-[#2E2A24]">
                {text}
            </pre>
            <button
                onClick={() =>
                    navigator.clipboard.writeText(text).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1600);
                    })
                }
                className="absolute right-2 top-2 rounded-[8px] bg-[#241C5C] px-2.5 py-1 font-hanken text-[12px] font-semibold text-white hover:bg-[#332a75]"
            >
                {copied ? 'Copié ✓' : 'Copier'}
            </button>
        </div>
    );
};

const SellerApiDocs = () => {
    const navigate = useNavigate();
    const [apiKey, setApiKey] = useState('');
    const [formFields, setFormFields] = useState<FormBuilderField[]>([]);
    const [industryName, setIndustryName] = useState('');
    const [subIndustryName, setSubIndustryName] = useState('');
    // The API always uses the account price (Mes prix) of the product sent.
    const [accountPrices, setAccountPrices] = useState<AccountPrice[]>([]);
    const [copiedAgent, setCopiedAgent] = useState(false);
    const { industries } = useIndustries();

    useEffect(() => {
        try {
            const saved = localStorage.getItem(API_KEY_STORAGE);
            if (saved) setApiKey(saved);
        } catch {
            /* storage unavailable: the key just isn't remembered */
        }
        fetchAccountPrices()
            .then(setAccountPrices)
            .catch(() => {});
        axios
            .get(`${CONFIG.apiUrl}/form-field`)
            .then((res) => setFormFields(res.data.results || []))
            .catch(() => {});
    }, []);

    const rememberKey = (value: string) => {
        setApiKey(value);
        try {
            if (value.trim()) localStorage.setItem(API_KEY_STORAGE, value.trim());
            else localStorage.removeItem(API_KEY_STORAGE);
        } catch {
            /* ignore */
        }
    };

    const selectedIndustry = industries.find((i) => i.name === industryName) || null;
    const subIndustryOptions = selectedIndustry?.subIndustries || [];
    const selectedSubIndustry = subIndustryOptions.find((s) => s.name === subIndustryName) || null;
    const subIndustryId = selectedSubIndustry?.subIndustryId ?? null;

    const mandatoryFieldIds = resolveMandatoryFields(selectedIndustry, subIndustryName || undefined);
    const optionalFieldIds = resolveFields(selectedIndustry, subIndustryName || undefined).filter((f) => !mandatoryFieldIds.includes(f));
    const fieldLabel = (fieldId: string) => formFields.find((f) => f.id === fieldId)?.name || fieldId;
    const mandatoryLabels = mandatoryFieldIds.map(fieldLabel);
    const optionalLabels = optionalFieldIds.map(fieldLabel);
    // An email-looking field defaults to the no-op test address, so copying the example never
    // creates a real, billable lead by accident.
    const exampleForField = (fieldId: string) => {
        const label = fieldLabel(fieldId);
        if (EMAIL_FIELD_REGEX.test(label)) return TEST_EMAIL;
        return fieldExampleValue(label, formFields.find((f) => f.id === fieldId));
    };

    const body = {
        subIndustryId: subIndustryId ?? '<subIndustryId>',
        ...Object.fromEntries(mandatoryFieldIds.map((id) => [fieldLabel(id), exampleForField(id)])),
    };
    const bodyExample = JSON.stringify(body, null, 2);
    const keyForExamples = apiKey || '<votre-clé-api>';
    const curlExample = `curl -X POST "${DISPLAY_API_URL}/lead/api/submit" \\
  -H "x-api-key: ${keyForExamples}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body)}'`;
    const price = selectedSubIndustry ? accountPriceFor(accountPrices, industryName, subIndustryName) : undefined;
    const agentInstructions = buildGenericApiAgentInstructions(subIndustryId, mandatoryLabels, optionalLabels);

    const select = 'rounded-[10px] border border-[#DDD5C9] bg-white px-3 py-2.5 font-hanken text-[14px] outline-none focus:border-[#241C5C] disabled:opacity-50';

    return (
        <div className="mx-auto flex max-w-[1000px] flex-col gap-5 pb-16 pt-2 font-hanken text-[#1A1613]">
            <button onClick={() => navigate('/integrations')} className="self-start text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                ← Mes intégrations
            </button>

            <header className="flex items-start gap-3.5">
                <KindLogo kind="api" />
                <div>
                    <h1 className="font-newsreader text-[34px] font-medium leading-[1.1] tracking-[-0.02em] text-[#14110E]">Envoi par API</h1>
                    <p className="mt-1.5 max-w-[720px] text-[14.5px] leading-relaxed text-[#6B6357]">
                        Envoyez vos leads à Leadrs depuis votre CRM, votre formulaire maison ou un outil d'automatisation (Zapier, Make, n8n…),
                        sans afficher de formulaire Leadrs. Une clé, une adresse, pour tous vos produits. Suivez les étapes dans l'ordre.
                    </p>
                </div>
            </header>

            <Step n={1} title="Votre clé API" intro="Elle identifie votre compte vendeur. Créez-la ici, puis gardez-la secrète : ne la publiez jamais dans un dépôt de code.">
                <SellerApiKeyPanel onKeyShown={rememberKey} />
                <div className="rounded-[12px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3 text-[13px] text-[#6B6357]">
                    {apiKey ? (
                        <span className="flex flex-wrap items-center gap-2">
                            ✓ Clé insérée dans les exemples et les tests de cette page :{' '}
                            <span className="font-jbmono text-[12.5px] text-[#2E2A24]">{apiKey.slice(0, 8)}…</span>
                            <button onClick={() => rememberKey('')} className="font-semibold text-[#241C5C] hover:underline">
                                Ne plus l'utiliser ici
                            </button>
                        </span>
                    ) : (
                        <span className="flex flex-wrap items-center gap-2">
                            Cliquez sur « Afficher » ci-dessus pour insérer votre clé dans les exemples, ou collez-la :
                            <input
                                onChange={(e) => rememberKey(e.target.value)}
                                placeholder="lrk_..."
                                aria-label="Clé API à utiliser dans les exemples"
                                className="min-w-[220px] flex-1 rounded-[9px] border border-[#DDD5C9] bg-white px-3 py-1.5 font-jbmono text-[12.5px] outline-none focus:border-[#241C5C]"
                            />
                        </span>
                    )}
                </div>
            </Step>

            <Step
                n={2}
                title="Choisissez le produit"
                intro={
                    <>
                        Chaque lead est envoyé pour un produit, identifié par son <code className={code}>subIndustryId</code>. Choisissez-le pour
                        voir son identifiant, les champs à envoyer et le prix minimum appliqué.
                    </>
                }
            >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <select
                        value={industryName}
                        onChange={(e) => {
                            setIndustryName(e.target.value);
                            setSubIndustryName('');
                        }}
                        className={select}
                        aria-label="Activité"
                    >
                        <option value="">Choisir une activité</option>
                        {industries.map((i) => (
                            <option key={i._id} value={i.name}>
                                {i.name}
                            </option>
                        ))}
                    </select>
                    <select value={subIndustryName} onChange={(e) => setSubIndustryName(e.target.value)} disabled={!selectedIndustry} className={select} aria-label="Produit">
                        <option value="">Choisir le produit</option>
                        {subIndustryOptions.map((s) => (
                            <option key={s.name} value={s.name}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
                {selectedSubIndustry &&
                    (subIndustryId == null ? (
                        <p className="text-[13.5px] text-[#A8332B]">Ce produit n'a pas encore d'identifiant : contactez Leadrs pour qu'il lui en soit attribué un.</p>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="rounded-[12px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3">
                                <span className="text-[12px] text-[#8A8172]">subIndustryId</span>
                                <b className="block font-jbmono text-[18px] text-[#241C5C]">{subIndustryId}</b>
                            </div>
                            <div className={`rounded-[12px] border px-4 py-3 ${price != null ? 'border-[#EDE7DE] bg-[#FAF8F4]' : 'border-[#F0E4C4] bg-[#FCF9EF]'}`}>
                                <span className="text-[12px] text-[#8A8172]">Prix minimum par lead</span>
                                <b className="block text-[18px]">{price != null ? `${price} €` : 'Pas de prix'}</b>
                                <Link to={PRICES_PATH} className="text-[12px] font-semibold text-[#241C5C] hover:underline">
                                    {price != null ? 'Prix du compte · Mes prix →' : 'À fixer dans Mes prix, sinon erreur 409 →'}
                                </Link>
                            </div>
                            <div className="rounded-[12px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3 text-[12.5px]">
                                <span className="text-[12px] text-[#8A8172]">Champs</span>
                                <p className="mt-0.5">
                                    <strong>Obligatoires :</strong> {mandatoryLabels.join(', ') || '—'}
                                </p>
                                {optionalLabels.length > 0 && (
                                    <p className="mt-0.5 text-[#6B6357]">
                                        <strong>Optionnels :</strong> {optionalLabels.join(', ')}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
            </Step>

            <Step
                n={3}
                title="Envoyez un lead"
                intro={
                    <>
                        Une requête <code className={code}>POST</code> par lead, avec votre clé dans l'en-tête <code className={code}>x-api-key</code> et
                        le corps en JSON.
                    </>
                }
            >
                <div className="rounded-[12px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3 font-jbmono text-[13px]">
                    <span className="font-bold text-[#146334]">POST</span> {DISPLAY_API_URL}/lead/api/submit
                </div>
                <div>
                    <p className="mb-1.5 text-[13px] font-semibold text-[#3B352C]">Corps de la requête</p>
                    <div className="mb-3 rounded-[12px] border border-[#D7D3F5] bg-[#F5F3FF] px-4 py-3 text-[13px] leading-relaxed text-[#3B352C]">
                        <strong className="text-[#241C5C]">Pourquoi ces champs ?</strong>{' '}
                        <code className={code}>subIndustryId</code> indique à Leadrs de quel produit il s'agit. Chaque produit a ensuite ses
                        propres <strong>champs obligatoires</strong> : ce sont les informations dont les acheteurs ont besoin pour traiter ce type de
                        demande
                        {selectedSubIndustry && mandatoryLabels.length > 0 ? (
                            <>
                                {' '}
                                (pour {subIndustryName} : {mandatoryLabels.join(', ')})
                            </>
                        ) : null}
                        . Le nom de chaque champ doit être écrit exactement comme dans la liste. Un lead incomplet risque d'être refusé ou de ne
                        trouver aucun acheteur.
                        <span className="mt-1.5 block">
                            Les champs obligatoires et optionnels de <strong>chaque produit</strong> sont donnés par l'endpoint{' '}
                            <code className={code}>GET /lead/api/products</code>, présenté juste en dessous.{' '}
                            <button
                                onClick={() => document.getElementById('liste-produits')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                className="font-semibold text-[#241C5C] hover:underline"
                            >
                                Voir la liste des produits et de leurs champs ↓
                            </button>
                        </span>
                    </div>
                    <CopyBlock text={bodyExample} />
                    <p className="mt-1.5 text-[12.5px] text-[#8A8172]">
                        Optionnel : ajoutez <code className={code}>landingPage</code> (l'adresse de la page d'où vient le lead) pour suivre son origine.
                    </p>
                </div>
                <div>
                    <p className="mb-1.5 text-[13px] font-semibold text-[#3B352C]">Exemple complet (cURL)</p>
                    <CopyBlock text={curlExample} />
                </div>
                <div className="rounded-[12px] border border-[#F0E4C4] bg-[#FCF9EF] px-4 py-3 text-[13px] leading-relaxed text-[#3B352C]">
                    <strong>Envoyez tous les champs du produit, même optionnels.</strong> Certains acheteurs filtrent selon une réponse (par
                    exemple, exclure les locataires) : un champ absent n'est pas filtré, et un lead filtré répond quand même{' '}
                    <code className={code}>200</code>. Son statut « bloqué » se voit dans Mes leads.
                </div>
            </Step>

            <Step
                n={4}
                title="Testez sans créer de vrai lead"
                intro={
                    <>
                        Mettez <code className={code}>{TEST_EMAIL}</code> dans n'importe quel champ, typiquement l'e-mail : la réponse est la même
                        qu'en vrai, mais le lead n'est jamais vendu ni facturé. Il apparaît dans Mes leads avec le statut « test ».
                    </>
                }
            >
                <SubmitLeadTester apiKey={apiKey} formFields={formFields} defaultSubIndustryId={subIndustryId} />
            </Step>

            <Step n={5} title="Lisez la réponse" intro="Le code HTTP dit ce qui s'est passé. Gérez au moins ces cas dans votre intégration.">
                <div className="overflow-hidden rounded-[12px] border border-[#EDE7DE]">
                    {[
                        ['200', 'Lead reçu : vendu à un acheteur, en attente de validation, ou gardé en attendant un acheteur.'],
                        ['400', 'Corps vide ou incomplet.'],
                        ['403', 'Clé absente ou invalide, ou compte vendeur bloqué.'],
                        ['404', 'subIndustryId inconnu.'],
                        ['409', 'Pas de prix minimum pour ce produit : fixez-le dans Mes prix. Rien à corriger dans le code.'],
                    ].map(([status, text]) => (
                        <div key={status} className="flex gap-4 border-b border-[#F1ECE4] px-4 py-2.5 text-[13.5px] last:border-0">
                            <span className={`w-10 shrink-0 font-jbmono font-bold ${status === '200' ? 'text-[#146334]' : 'text-[#A8332B]'}`}>{status}</span>
                            <span className="text-[#3B352C]">{text}</span>
                        </div>
                    ))}
                </div>
            </Step>

            <section id="liste-produits" className={`${card} scroll-mt-6`}>
                <h2 className="font-newsreader text-[24px] font-medium leading-tight text-[#14110E]">La liste des produits et de leurs champs</h2>
                <p className="mt-1 max-w-[720px] text-[14px] leading-relaxed text-[#6B6357]">
                    Pour chaque produit (<code className={code}>subIndustryId</code>), cet endpoint donne les champs obligatoires et optionnels à
                    envoyer à l'étape 3, avec leur nom exact. Utilisez-le pour les découvrir ou les vérifier dans votre code plutôt que de les
                    recopier à la main. Testez-le ci-dessous : laissez <code className={code}>subIndustryId</code> vide pour tout lister.
                </p>
                <div className="mt-4">
                    <ApiEndpointPlayground
                        method="GET"
                        title="Lister les produits et leurs champs"
                        description="Retourne, pour chaque subIndustryId, les champs obligatoires et optionnels attendus par POST /lead/api/submit."
                        pathTemplate="/lead/api/products"
                        queryParams={[
                            {
                                name: 'subIndustryId',
                                label: 'subIndustryId',
                                placeholder: 'laisser vide pour tout lister',
                                defaultValue: subIndustryId != null ? String(subIndustryId) : '',
                            },
                        ]}
                        requiredScope="aucun (la clé authentifie directement votre compte vendeur)"
                        apiKey={apiKey}
                    />
                </div>
            </section>

            <section className={card}>
                <h2 className="font-newsreader text-[24px] font-medium leading-tight text-[#14110E]">Vous développez avec une IA ?</h2>
                <p className="mt-1 max-w-[720px] text-[14px] leading-relaxed text-[#6B6357]">
                    Cursor, Claude Code, Copilot… Donnez-lui ces instructions : elles reprennent les étapes ci-dessus pour le produit choisi, avec
                    le lien vers la documentation complète.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        onClick={() =>
                            navigator.clipboard.writeText(agentInstructions).then(() => {
                                setCopiedAgent(true);
                                setTimeout(() => setCopiedAgent(false), 1600);
                            })
                        }
                        className="rounded-[9px] bg-[#241C5C] px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-[#332a75]"
                    >
                        {copiedAgent ? 'Copié ✓' : 'Copier les instructions pour l’IA'}
                    </button>
                    <a href={GENERIC_API_MD_DOC_URL} target="_blank" rel="noreferrer" className="rounded-[9px] border border-[#DDD5C9] px-3 py-2 text-[12.5px] font-semibold text-[#3B352C] hover:border-[#241C5C]">
                        Ouvrir la documentation complète (.md)
                    </a>
                </div>
            </section>
        </div>
    );
};

export default SellerApiDocs;
